import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Y2CurrentProfitFirst from 'calculations/Y2CurrentProfitFirst';
import { Y2CurrentProfitSecond } from 'calculations/Y2CurrentProfitSecond';
import Y3CurrentProfitFirst from 'calculations/Y3CurrentProfitFirst';
import { Y3CurrentProfitSecond } from 'calculations/Y3CurrentProfitSecond';
import Y4CurrentProfitFirst from 'calculations/Y4CurrentProfitFirst';
import { Y4CurrentProfitSecond } from 'calculations/Y4CurrentProfitSecond';
import Y5CurrentProfitFirst from 'calculations/Y5CurrentProfitFirst';
import { Y5CurrentProfitSecond } from 'calculations/Y5CurrentProfitSecond';
import {
  BoardMineralization,
  BoardSoil,
  BoardStatus,
} from 'features/Croptimizer/types';
import { numericValues } from 'utils/helpers';
import {
  getEfficiencyFormula,
  getYCurrentProfitHelper,
} from 'features/Croptimizer/utils/calcHelperBoard';
import { useGermanStatusBoard } from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/1Status/German/hooks/useGermanStatusBoard';

interface Props {
  boardStatusValues: BoardStatus | null;
  mineralizationValues: BoardMineralization | null;
  boardSoilValues: BoardSoil | null;
}

const useCurrentProfit = ({
  boardStatusValues,
  mineralizationValues,
  boardSoilValues,
}: Props) => {
  const { t } = useTranslation();

  const { getOptimumValue } = useGermanStatusBoard();
  
  const getY1CurrentProfit = useMemo(() => {
    if (boardStatusValues && mineralizationValues && boardSoilValues) {
      if (boardStatusValues?.currentPh < boardStatusValues?.targetPh) {
        if (mineralizationValues?.cap === t('no')) {
          return getYCurrentProfitHelper(
            boardStatusValues?.currentPh,
            boardStatusValues?.targetPh,
            boardSoilValues?.dose,
            Number(boardSoilValues?.split),
          );
        } else {
          const ag38 = getEfficiencyFormula(
            Number(mineralizationValues?.month),
            Number(boardStatusValues?.currentPh),
            Number(numericValues(boardStatusValues?.currentSec)),
            // Number(boardSoilValues?.selected),
            Number(getOptimumValue),
            Number(numericValues(mineralizationValues?.soilTillage)),
          );
          const ah78 = ag38 / 100;
          const ah64 = ah78 / Number(boardSoilValues?.split);
          const result =
            Number(boardStatusValues?.currentPh) +
            (Number(boardStatusValues?.targetPh) -
              Number(boardStatusValues?.currentPh)) *
              ah64;

          return result.toFixed(2);
        }
      } else {
        const calculated =
          Number(mineralizationValues?.annualDecrease) *
          (Number(numericValues(boardSoilValues?.dose)) / 100) *
          Number(boardSoilValues?.split);

        if (mineralizationValues?.cap === t('no')) {
          return (
            Number(boardStatusValues?.targetPh) -
            Number(mineralizationValues?.annualDecrease) +
            calculated
          ).toFixed(2);
        } else {
          const ag38 = getEfficiencyFormula(
            Number(mineralizationValues?.month),
            Number(boardStatusValues?.currentPh),
            Number(numericValues(boardStatusValues?.currentSec)),
            //Number(boardSoilValues?.selected),
            Number(getOptimumValue),
            Number(numericValues(mineralizationValues?.soilTillage)),
          );

          const calculatedWithAg28 = calculated * (ag38 / 100);

          return (
            Number(boardStatusValues?.targetPh) -
            Number(mineralizationValues?.annualDecrease) +
            calculatedWithAg28
          ).toFixed(2);
        }
      }
    }

    return '';
  }, [boardStatusValues, mineralizationValues, boardSoilValues]);

  const { result: Y2CurrentProfitFirstResult } = Y2CurrentProfitFirst({
    getY1CurrentProfit,
    mineralizationValues,
    boardStatusValues,
    boardSoilValues,
    optimumValue: Number(getOptimumValue),
    t,
  });

  const getProfitResult = (
    check: string | undefined,
    first: string | undefined,
    second: () => string | undefined,
  ) => {
    if (check && mineralizationValues && boardStatusValues && boardSoilValues) {
      if (boardStatusValues?.currentPh < boardStatusValues?.targetPh) {
        return first;
      } else {
        return second();
      }
    }
  };

  const getY2CurrentProfit = getProfitResult(
    getY1CurrentProfit,
    Y2CurrentProfitFirstResult,
    () => Y2CurrentProfitSecond({
      getY1CurrentProfit,
      mineralizationValues,
      boardStatusValues,
      boardSoilValues,
      optimumValue: Number(getOptimumValue),
      t,
    }),
  );

  const { result: Y3CurrentProfitFirstResult } = Y3CurrentProfitFirst({
    getY2CurrentProfit,
    mineralizationValues,
    boardStatusValues,
    boardSoilValues,
    optimumValue: Number(getOptimumValue),
    t,
  });

  const getY3CurrentProfit = getProfitResult(
    getY2CurrentProfit,
    Y3CurrentProfitFirstResult,
    () => Y3CurrentProfitSecond({
      getY2CurrentProfit,
      mineralizationValues,
      boardStatusValues,
      boardSoilValues,
      optimumValue: Number(getOptimumValue),
      t,
    }),
  );

  const { result: Y4CurrentProfitFirstResult } = Y4CurrentProfitFirst({
    getY3CurrentProfit,
    mineralizationValues,
    boardStatusValues,
    boardSoilValues,
    optimumValue: Number(getOptimumValue),
    t,
  });

  const getY4CurrentProfit = getProfitResult(
    getY3CurrentProfit,
    Y4CurrentProfitFirstResult,
    () => Y4CurrentProfitSecond({
      getY3CurrentProfit,
      mineralizationValues,
      boardStatusValues,
      boardSoilValues,
      optimumValue: Number(getOptimumValue),
      t,
    }),
  );

  const { result: Y5CurrentProfitFirstResult } = Y5CurrentProfitFirst({
    getY4CurrentProfit,
    mineralizationValues,
    boardStatusValues,
    boardSoilValues,
    optimumValue: Number(getOptimumValue),
    t,
  });

  const getY5CurrentProfit = getProfitResult(
    getY4CurrentProfit,
    Y5CurrentProfitFirstResult,
    () => Y5CurrentProfitSecond({
      getY4CurrentProfit,
      mineralizationValues,
      boardStatusValues,
      boardSoilValues,
      optimumValue: Number(getOptimumValue),
      t,
    }),
  );

  return {
    getY1CurrentProfit,
    getY2CurrentProfit,
    getY3CurrentProfit,
    getY4CurrentProfit,
    getY5CurrentProfit,
  };
};

export default useCurrentProfit;
